import { FC, Fragment, useEffect, useState } from 'react';
import { AlpDeal, PageItem, PelegGroup, Tab } from '../types';
import { Button, SelectCustom } from '@bepro-travel/fe.ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faHotel, faImages, faTimes, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { formatPriceCurrency, openInNewWindow } from '@bepro-travel/fe.utils';
import { useModalClass } from '@bepro-travel/fe.hooks';
import Slider from 'react-slick';
import { ScreenshotSupport } from '@bepro-travel/fe.shared';
import { cdnUrl } from '../constants';

interface SalesCmsProps {
  sales: PageItem[];
  onImageLoadError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const Sales: FC<SalesCmsProps> = ({ sales, onImageLoadError }) => {
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [openModalIndex, setOpenModalIndex] = useState(-1);
  const classList = useModalClass(openModalIndex !== -1);
  const [salesState, setSalesState] = useState<PageItem[]>([]);
  const blobPath = '/backend';

  useEffect(() => {
    const newSales = sales.map((sale) => {
      const newTabs = sale.sales?.tabs.map((tab) => {
        const newPelegGroups = tab.pelegSales.reduce((acc: PelegGroup[], sale) => {
          const index = acc.findIndex((item) => item.deal.hotelName === sale.hotelName);

          const dates = {
            label: `${sale.checkIn} - ${sale.checkOut}`,
            value: `${sale.routingValues.checkIn}`,
            data: {
              checkIn: sale.checkIn,
              checkOut: sale.checkOut,
              pricePerPerson: sale.pricePerPerson,
              totalPrice: sale.totalPrice,
              boardDescription: sale.boardDescription,
              roomDescription: sale.roomDescription,
              nights: sale.nights,
              routingValues: sale.routingValues,
              status: sale.status,
            },
          };
          if (index === -1) {
            acc.push({
              dates: [dates],
              deal: sale,
              selected: dates.value,
              pricePerPerson: dates.data.pricePerPerson,
              totalPrice: dates.data.totalPrice,
              boardDescription: dates.data.boardDescription,
              roomDescription: dates.data.roomDescription,
              nights: dates.data.nights,
              routingValues: dates.data.routingValues,
              status: dates.data.status,
              allPhotos: sale.images,
              hotelName: sale.hotelName,
              checkIn: sale.routingValues.checkIn,
              checkOut: sale.checkOut,
            });
          } else if (!acc?.[index]?.dates?.some((d: any) => d.value === dates.value)) {
            acc[index].dates.push({
              ...dates,
              value: dates.value,
            });
          }

          return acc;
        }, []);

        const sortedNewPelegGroups = newPelegGroups.map((item) => {
          const sortedDates = item.dates.sort((a, b) => {
            const [aday, amonth, ayear] = (a.value as string).split('-').map(Number);
            const [bday, bmonth, byear] = (b.value as string).split('-').map(Number);
            return (
              new Date(ayear, amonth - 1, aday).getTime() -
              new Date(byear, bmonth - 1, bday).getTime()
            );
          });

          const firstNonRQDate = sortedDates.find((date) => date.data.status !== 'RQ');
          const selectedDate = firstNonRQDate || sortedDates[0];
          return {
            ...item,
            dates: sortedDates,
            selected: selectedDate.value.toString(),
            pricePerPerson: selectedDate.data.pricePerPerson,
            totalPrice: selectedDate.data.totalPrice,
            boardDescription: selectedDate.data.boardDescription,
            roomDescription: selectedDate.data.roomDescription,
            nights: selectedDate.data.nights,
            routingValues: selectedDate.data.routingValues,
            status: selectedDate.data.status,
          };
        });

        return {
          ...tab,
          pelegGroups: sortedNewPelegGroups,
        };
      });

      return {
        ...sale,
        sales: {
          ...sale.sales,
          tabs: newTabs!,
        },
      };
    });

    setSalesState(newSales);
  }, [sales]);

  const onClickPeleg = (routingValues: any, otherOptions: any, pg: any) => {
    const search = new URLSearchParams({ ...routingValues, isSale: true } as any).toString();
    sessionStorage.setItem('options_' + routingValues.dealId + routingValues.packageId, JSON.stringify(otherOptions));

    sessionStorage.setItem('search' + routingValues.dealId + routingValues.cityCode + routingValues.checkIn, JSON.stringify(pg));
    openInNewWindow('/tourru/details?' + search);
  };

  const onSelectPelegDate = (
    index: number,
    value: string,
    mainIndex: number,
    saleIndex: number
  ) => {
    const newSales = salesState.map((sale, mIndex) => {
      if (mIndex !== mainIndex) {
        return sale;
      }
      const newTabs = sale.sales?.tabs.map((tab, sIndex) => {
        if (sIndex !== saleIndex) {
          return tab;
        }

        const newPelegGroups = tab.pelegGroups?.map((group, groupIndex) => {
          if (groupIndex === index) {
            const dataVal = group.dates.find((dat) => dat.value === value);

            return {
              ...group,
              selected: value,
              pricePerPerson: dataVal?.data.pricePerPerson,
              totalPrice: dataVal?.data.totalPrice,
              boardDescription: dataVal?.data.boardDescription,
              roomDescription: dataVal?.data.roomDescription,
              nights: dataVal?.data.nights,
              routingValues: dataVal?.data.routingValues,
              status: dataVal?.data.status,
            };
          }

          return group;
        });

        return {
          ...tab,
          pelegGroups: newPelegGroups,
        };
      });

      return {
        ...sale,
        sales: {
          ...sale.sales,
          tabs: newTabs!,
        },
      };
    });

    setSalesState(newSales);
  };

  return (
    <Fragment>
      {salesState.map((sale, index) => (
        <Fragment key={index}>
          {sale.sales?.tabs.map((tab: any, saleIndex: number) => (
            <div
              style={{ order: sales?.[0]?.orderDisplay }}
              key={saleIndex}
              className="main-page-section"
            >
              {tab.tab.title.desc && <div className="title">{tab.tab.title.desc}</div>}
              <div className="row">
                {tab.pelegGroups?.map(
                  ({ deal, dates, selected, ...pg }: any, dealIndex: number) => (
                    <div className="col-md-3 position-relative" key={dealIndex}>
                      <div className="gallery-ico" onClick={() => setOpenModalIndex(dealIndex)}>
                        <FontAwesomeIcon icon={faImages as any} />
                        Галерея
                      </div>
                      <div className="sale" onClick={() => onClickPeleg(pg.routingValues, { dates }, pg)}>
                        {pg.status === 'RQ' && (
                          <img
                            className="sale-sold"
                            src={require('../assets/img/sold_out.png')}
                            alt=""
                          />
                        )}
                        <img
                          src={cdnUrl + blobPath + deal.mainImageLink}
                          alt="deal"
                          onError={onImageLoadError}
                        />
                        <div className="title-light text-center text-xl mt-30p mb-10 ph-10">
                          {deal.hotelName}
                        </div>
                        <div className="sale-footer">
                          <div className="mr-10">
                            <div onClick={(e) => e.stopPropagation()}>
                              <SelectCustom
                                options={dates}
                                value={selected}
                                onChange={(val) =>
                                  onSelectPelegDate(dealIndex, val, index, saleIndex)
                                }
                                appendToBody
                                menuPortralClassName="select-custom__menu-peleg"
                                isDropdown={true}
                              />
                            </div>
                            <div className="display-flex">
                              <FontAwesomeIcon icon={faHotel as any} className="mr-5" />
                              {pg.roomDescription}
                            </div>
                            <div className="display-flex">
                              <FontAwesomeIcon icon={faUtensils as any} className="mr-5" />
                              {pg.boardDescription}
                            </div>
                            <div className="display-flex">
                              <FontAwesomeIcon icon={faBed as any} className="mr-5" />
                              {pg.nights} {translate('nights')}
                            </div>
                          </div>
                          <div className="text-right">
                            <div>{translate('package.price_per_person')} </div>
                            <div className="text-st text-bold">
                              {formatPriceCurrency(pg.pricePerPerson, deal.currencySymbol)}
                            </div>
                            <div>{translate('total')} </div>
                            <div className="text-st text-bold">
                              {formatPriceCurrency(pg.totalPrice, deal.currencySymbol)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {openModalIndex === dealIndex && (
                        <div
                          className="modal-overlay modal-overlay-top"
                          onClick={(e) => e.target === e.currentTarget && setOpenModalIndex(-1)}
                        >
                          <div className={classList} style={{ width: '80%' }}>
                            <div className="modal-title mb-20">
                              <h5 className="title">{translate('gallery')}</h5>
                              <div>
                                <ScreenshotSupport />
                                <FontAwesomeIcon
                                  icon={faTimes as any}
                                  onClick={() => setOpenModalIndex(-1)}
                                />
                              </div>
                            </div>

                            <div className="modal-body gallery__wr">
                              <Slider arrows={true} dots>
                                {deal.images.map((image: any, index: number) => (
                                  <div key={index} className="slider-main-image">
                                    <span className="slider-main-image-title display-ib mb-20">
                                      {image.alt}
                                    </span>
                                    <img src={cdnUrl + blobPath + image.link} alt={image.alt} />
                                  </div>
                                ))}
                              </Slider>
                            </div>

                            <div className="modal-footer">
                              <Button
                                title={translate('close')}
                                className="btn btn-default"
                                onClick={() => setOpenModalIndex(-1)}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </Fragment>
      ))}
    </Fragment>
  );
};

export default Sales;
